import { z } from 'zod';
import { BusinessCodeSchema, EnteredNameSchema, RecordIdSchema } from './identifiers';
import {
  MoneySchema,
  compareMoney,
  divideMoney,
  money,
  multiplyMoney,
  roundMoney,
  subtractMoney,
  type Money,
} from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Fixed assets — BMP-2 package 5 (`FIN-ASSET-001`, `FIN-ASSET-002`).
 *
 * An asset belongs to a category; **depreciation is configured per category with no default**
 * (`BD-42`): a category without a method, a useful life and a residual value is not depreciated, and
 * says so. When an asset is put in service its category's terms are copied onto it, so a later change
 * of the category never rewrites an asset's history.
 *
 * A depreciation run covers one legal entity and one month. It is prepared (computed, nothing stored on
 * the assets), reviewed, and posted — once: a second run for the same month is refused. Posting adds
 * each line to its asset's accumulated depreciation and records `asset.depreciated` in the same
 * transaction. Disposal computes the gain or loss against the book value and records
 * `asset.disposed`. Impairment is a boundary: it is not built.
 *
 * All arithmetic is exact decimal; monthly amounts are rounded half-up to two places and the last
 * month takes whatever is left, so an asset never goes below its residual value.
 */

export const DEPRECIATION_METHODS = ['straightLine', 'decliningBalance'] as const;
export const DepreciationMethodSchema = z.enum(DEPRECIATION_METHODS);
export type DepreciationMethod = z.infer<typeof DepreciationMethodSchema>;

const ZeroToHundred = z.string().regex(/^\d{1,2}(\.\d{1,4})?$/, { message: 'PERCENT_EXPECTED' });
const PositivePercent = ZeroToHundred.refine((value) => Number(value) > 0, {
  message: 'PERCENT_EXPECTED',
});

export const DepreciationTermsSchema = z
  .strictObject({
    method: DepreciationMethodSchema,
    usefulLifeMonths: z.number().int().min(1).max(1200),
    /** Residual value as a percentage of cost; 0 allowed. */
    residualPercent: ZeroToHundred,
    /** Annual rate for the declining-balance method. */
    annualRatePercent: PositivePercent.optional(),
  })
  .refine((value) => value.method !== 'decliningBalance' || !!value.annualRatePercent, {
    message: 'RATE_REQUIRED',
    path: ['annualRatePercent'],
  });
export type DepreciationTerms = z.infer<typeof DepreciationTermsSchema>;

export const ASSET_CATEGORY_STATES = ['active', 'inactive'] as const;

export const AssetCategorySchema = z.strictObject({
  categoryId: RecordIdSchema,
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  legalEntityId: RecordIdSchema,
  /** `null`: not configured (BD-42) — assets of this category are not depreciated. */
  depreciation: DepreciationTermsSchema.nullable(),
  state: z.enum(ASSET_CATEGORY_STATES),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type AssetCategory = z.infer<typeof AssetCategorySchema>;

export const CreateAssetCategorySchema = z.strictObject({
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  legalEntityId: RecordIdSchema,
  depreciation: DepreciationTermsSchema.nullable().default(null),
});
export const UpdateAssetCategorySchema = z.strictObject({
  name: EnteredNameSchema.optional(),
  depreciation: DepreciationTermsSchema.nullable().optional(),
  state: z.enum(ASSET_CATEGORY_STATES).optional(),
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

export const ASSET_STATES = ['draft', 'inService', 'disposed'] as const;
export const AssetStateSchema = z.enum(ASSET_STATES);
export type AssetState = z.infer<typeof AssetStateSchema>;

export const ASSET_EVENT_KINDS = [
  'registered',
  'placedInService',
  'transferred',
  'maintenance',
  'depreciated',
  'disposed',
] as const;

export const AssetEventSchema = z.strictObject({
  kind: z.enum(ASSET_EVENT_KINDS),
  on: BusinessDateSchema,
  byAccountId: z.string(),
  note: z.string().optional(),
  amount: MoneySchema.optional(),
  /** Transfers: where it was and where it went. */
  from: z
    .strictObject({
      branchId: z.string().optional(),
      location: z.string().optional(),
      custodianAccountId: z.string().optional(),
    })
    .optional(),
  to: z
    .strictObject({
      branchId: z.string().optional(),
      location: z.string().optional(),
      custodianAccountId: z.string().optional(),
    })
    .optional(),
  at: InstantSchema,
});
export type AssetEvent = z.infer<typeof AssetEventSchema>;

export const AssetSchema = z.strictObject({
  assetId: RecordIdSchema,
  number: z.string(),
  name: EnteredNameSchema,
  categoryId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  departmentId: RecordIdSchema.optional(),
  location: z.string().optional(),
  custodianAccountId: z.string().optional(),
  serialNumber: z.string().optional(),
  vendorInvoiceId: RecordIdSchema.optional(),
  acquiredOn: BusinessDateSchema,
  cost: MoneySchema,
  warrantyUntil: BusinessDateSchema.optional(),
  warrantyProvider: z.string().optional(),
  inServiceOn: BusinessDateSchema.optional(),
  /** First month depreciated (`YYYY-MM`), fixed at the first posting by the start rule (BD-68). */
  depreciationStart: z.string().optional(),
  /** The category's terms when put in service; absent = not depreciated (BD-42 unset). */
  depreciation: DepreciationTermsSchema.optional(),
  accumulated: MoneySchema,
  bookValue: MoneySchema,
  /** Last month depreciated, `YYYY-MM`. */
  depreciatedThrough: z.string().optional(),
  monthsDepreciated: z.number().int().nonnegative(),
  state: AssetStateSchema,
  /** A disposal waiting for approval (finance.asset.dispose). */
  pendingDisposal: z
    .strictObject({ disposedOn: BusinessDateSchema, proceeds: MoneySchema, reason: z.string() })
    .optional(),
  disposal: z
    .strictObject({
      disposedOn: BusinessDateSchema,
      proceeds: MoneySchema,
      gain: MoneySchema,
      loss: MoneySchema,
      reason: z.string(),
    })
    .optional(),
  events: z.array(AssetEventSchema),
  createdByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Asset = z.infer<typeof AssetSchema>;

export const CreateAssetSchema = z.strictObject({
  name: EnteredNameSchema,
  categoryId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  departmentId: RecordIdSchema.optional(),
  location: z.string().trim().min(1).max(200).optional(),
  custodianAccountId: z.string().min(1).max(200).optional(),
  serialNumber: z.string().trim().min(1).max(100).optional(),
  vendorInvoiceId: RecordIdSchema.optional(),
  acquiredOn: BusinessDateSchema,
  cost: MoneySchema,
  warrantyUntil: BusinessDateSchema.optional(),
  warrantyProvider: z.string().trim().min(2).max(160).optional(),
  idempotencyKey: z.string().trim().min(8).max(200),
});
export type CreateAsset = z.infer<typeof CreateAssetSchema>;

export const PlaceInServiceSchema = z.strictObject({
  inServiceOn: BusinessDateSchema,
  /** The ledger account the acquisition was settled from, for asset.acquired; optional. */
  settlementAccountId: RecordIdSchema.optional(),
  expectedVersion: z.number().int().positive(),
});

export const TransferAssetSchema = z
  .strictObject({
    on: BusinessDateSchema,
    branchId: RecordIdSchema.optional(),
    location: z.string().trim().min(1).max(200).optional(),
    custodianAccountId: z.string().min(1).max(200).nullable().optional(),
    note: z.string().trim().min(3).max(500),
    expectedVersion: z.number().int().positive(),
  })
  .refine(
    (value) =>
      value.branchId !== undefined ||
      value.location !== undefined ||
      value.custodianAccountId !== undefined,
    { message: 'NOTHING_TO_TRANSFER', path: ['branchId'] },
  );

export const RecordMaintenanceSchema = z.strictObject({
  on: BusinessDateSchema,
  note: z.string().trim().min(3).max(500),
  amount: MoneySchema.optional(),
  expectedVersion: z.number().int().positive(),
});

export const DisposeAssetSchema = z.strictObject({
  disposedOn: BusinessDateSchema,
  proceeds: MoneySchema,
  reason: z.string().trim().min(3).max(500),
  expectedVersion: z.number().int().positive(),
});

export const AssetQuerySchema = z.strictObject({
  legalEntityId: RecordIdSchema.optional(),
  categoryId: RecordIdSchema.optional(),
  state: AssetStateSchema.optional(),
  search: z.string().trim().min(1).max(60).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type AssetQuery = z.infer<typeof AssetQuerySchema>;

/* ------------------------------------------------------------------ runs */

export const MonthPeriodSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, { message: 'PERIOD_EXPECTED' });

export const DEPRECIATION_RUN_STATES = ['prepared', 'posted', 'discarded'] as const;

export const DepreciationLineSchema = z.strictObject({
  assetId: RecordIdSchema,
  number: z.string(),
  name: z.string(),
  amount: MoneySchema,
  accumulatedAfter: MoneySchema,
  bookValueAfter: MoneySchema,
});

export const DepreciationRunSchema = z.strictObject({
  runId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  period: MonthPeriodSchema,
  state: z.enum(DEPRECIATION_RUN_STATES),
  lines: z.array(DepreciationLineSchema),
  total: z.array(MoneySchema),
  /** Assets in service that were left out because their category has no terms (BD-42). */
  notConfigured: z.number().int().nonnegative(),
  preparedByAccountId: z.string(),
  postedByAccountId: z.string().optional(),
  postedAt: InstantSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
});
export type DepreciationRun = z.infer<typeof DepreciationRunSchema>;

export const PrepareRunSchema = z.strictObject({
  legalEntityId: RecordIdSchema,
  period: MonthPeriodSchema,
});
export const RunActionSchema = z.strictObject({
  expectedVersion: z.number().int().positive(),
});

/* ------------------------------------------------------------- arithmetic */

/** The residual value of an asset under its terms, half-up to two places. */
export function residualValue(cost: Money, terms: DepreciationTerms): Money {
  return roundMoney(
    divideMoney(multiplyMoney(cost, terms.residualPercent), '100', 10, 'halfUp'),
    2,
    'halfUp',
  );
}

/**
 * One month's depreciation of an asset, exactly: straight line spreads cost less residual over the
 * useful life; declining balance applies the annual rate ÷ 12 to the book value. Never below the
 * residual value; in the last month of the useful life everything down to the residual is taken.
 */
export function monthlyDepreciation(input: {
  cost: Money;
  accumulated: Money;
  terms: DepreciationTerms;
  /** Months already depreciated before this one. */
  monthsDone: number;
}): Money {
  const { cost, accumulated, terms, monthsDone } = input;
  const residual = residualValue(cost, terms);
  const book = subtractMoney(cost, accumulated);
  const room = subtractMoney(book, residual);
  const zero = money('0', cost.currency);
  if (compareMoney(room, zero) <= 0) return zero;
  if (monthsDone + 1 >= terms.usefulLifeMonths) return room;
  const raw =
    terms.method === 'straightLine'
      ? divideMoney(subtractMoney(cost, residual), String(terms.usefulLifeMonths), 2, 'halfUp')
      : roundMoney(
          divideMoney(multiplyMoney(book, terms.annualRatePercent ?? '0'), '1200', 10, 'halfUp'),
          2,
          'halfUp',
        );
  return compareMoney(raw, room) > 0 ? room : raw;
}

/** Months between two periods, `YYYY-MM`, inclusive of neither: 2026-01 → 2026-03 = 2. */
export function monthsBetween(from: string, to: string): number {
  const [fy, fm] = from.split('-').map(Number) as [number, number];
  const [ty, tm] = to.split('-').map(Number) as [number, number];
  return (ty - fy) * 12 + (tm - fm);
}

export const ASSET_APPROVAL_OPERATIONS = {
  /** Disposal of an asset: proceeds at once without a policy. */
  dispose: 'finance.asset.dispose',
} as const;

export const ASSET_AUDIT_ACTIONS = {
  categoryCreated: 'finance.asset.categoryCreated',
  categoryUpdated: 'finance.asset.categoryUpdated',
  registered: 'finance.asset.registered',
  placedInService: 'finance.asset.placedInService',
  transferred: 'finance.asset.transferred',
  maintenance: 'finance.asset.maintenance',
  disposed: 'finance.asset.disposed',
  runPrepared: 'finance.asset.depreciationPrepared',
  runPosted: 'finance.asset.depreciationPosted',
  runDiscarded: 'finance.asset.depreciationDiscarded',
} as const;
